interface LoadingData {
  [key: string]: boolean|undefined;
  [index: number]: boolean|undefined;
}

/**
 * This is a loading service that can have events tied to things loading or not loading.
 * This class is not meant to be injectable.
 *
 * @template T This is to specify the possible loading ids.  You can specify them in one of 2 ways:
 *  - Explicity: for example: `new LoadingService<['familyMembers', 'familyDetails', 'ledgerInfo']>();`, this is recommended, and allows
 *      for better type checking.
 *  - Implicity: for example: `new LoadingService<string[]>();`, this is not recommended, and there is no checking on the loadingIds.
 */
export class LoadingService<T extends (string|number)[]> {
  private _onNextAnyLoading: false | ((this: void, loadingId: T[number]) => void) = false;
  private _onAnyLoading: false | ((this: void, loadingId: T[number]) => void) = false;
  private _onAllLoading: false | ((this: void) => void) = false;
  private _onNextAnyDoneLoading: false | ((this: void, loadingId: T[number]) => void) = false;
  private _onAnyDoneLoading: false | ((this: void, loadingId: T[number]) => void) = false;
  private _onNextAllDoneLoading: false | ((this: void) => void) = false;
  private _onAllDoneLoading: false | ((this: void) => void) = false;
  private _loading: LoadingData;

  constructor() {
    this._loading = (new Object() as LoadingData);
  }

  /**
   * Set an action to happen only the next time something starts loading (the action only runs once and then unsets itself).
   */
  public set onNextAnyLoading(action: false | ((this: void, loadingId: T[number]) => void)) {
    this._onNextAnyLoading = action;
  }

  /**
   * Set an action to happen any time something starts loading.
   */
  public set onAnyLoading(action: false | ((this: void, loadingId: T[number]) => void)) {
    this._onAnyLoading = action;
  }

  /**
   * Set an action to happen when everything has started loading, but nothing is done.
   */
  public set onAllLoading(action: false | ((this: void) => void)) {
    this._onAllLoading = action;
  }

  /**
   * Set an action to happen only the next time anything is done loading (the action only runs once and then unsets itself).
   */
  public set onNextAnyDoneLoading(action: false | ((this: void, loadingId: T[number]) => void)) {
    this._onNextAnyDoneLoading = action;
  }

  /**
   * Set an action to happen when anything is done loading.
   */
  public set onAnyDoneLoading(action: false | ((this: void, loadingId: T[number]) => void)) {
    this._onAnyDoneLoading = action;
  }

  /**
   * Set an action to happen only the next time everything is done loading (the action only runs once and then unsets itself).
   */
  public set onNextAllDoneLoading(action: false | ((this: void) => void)) {
    this._onNextAllDoneLoading = action;
  }

  /**
   * Set an action to happen when everything is done loading.
   */
  public set onAllDoneLoading(action: false | ((this: void) => void)) {
    this._onAllDoneLoading = action;
  }

  /**
   * Checks the loading status.
   *
   * @param loadingId - The loading ids to check if any are loading. If not specified this will return if anything is loading.
   * @return boolean
   */
  public isLoading(...loadingIds: T[number][]): boolean {
    if (loadingIds.length === 0) {
      return Object.values(this._loading).some(v => !!v);
    } else {
      return Object.keys(this._loading).some(
        (loadingId) => loadingIds.indexOf(loadingId) >= 0 && !!this._loading[loadingId]
      );
    }
  }

  /**
   * Checks the loading status except for certian loading ids.
   *
   * @param loadingIds - The loading ids to exclude in the check if is loading.
   * @return boolean
   */
  public isLoadingExcept(...loadingIds: T[number][]): boolean {
    return Object.keys(this._loading).some(
      (loadingId) => loadingIds.indexOf(loadingId) < 0 && !!this._loading[loadingId]
    );
  }

  /**
   * Start loading something. This will trigger events as approriate.
   *
   * @remarks
   * #### Order of execution for events: ####
   * - `onAnyLoading`
   * - `onNextAnyLoading`
   * - `onAllLoading`
   *
   */
  public start(...loadingIds: T[number][]): void {
    for (const loadingId of loadingIds) {
      this._loading[loadingId] = true;
      if (this._onAnyLoading !== false) {
        this._onAnyLoading(loadingId);
      }
      if (this._onNextAnyLoading !== false) {
        this._onNextAnyLoading(loadingId);
        this._onNextAnyLoading = false;
      }
      if (this._onAllLoading !== false && !Object.values(this._loading).some(v => !v)) {
        this._onAllLoading();
      }
    }
  }

  /**
   * Done loading something. This will trigger events as approriate.
   *
   * @remarks
   * #### Order of execution for events: ####
   * - `onAnyDoneLoading`
   * - `onNextAnyDoneLoading`
   * - `onAllDoneLoading`
   * - `onNextAllDoneLoading`
   */
  public done(...loadingIds: T[number][]): void {
    for (const loadingId of loadingIds) {
      this._loading[loadingId] = false;
      if (this._onAnyDoneLoading !== false) {
        this._onAnyDoneLoading(loadingId);
      }
      if (this._onNextAnyDoneLoading !== false) {
        this._onNextAnyDoneLoading(loadingId);
        this._onNextAnyDoneLoading = false;
      }
      if (!Object.values(this._loading).some(v => !!v)) {
        if (this._onAllDoneLoading !== false) {
          this._onAllDoneLoading();
        }
        if (this._onNextAllDoneLoading !== false) {
          this._onNextAllDoneLoading();
          this._onNextAllDoneLoading = false;
        }
      }
    }
  }

  /**
   * A wrapper for an action to automatically start and stop loading.
   * @param loadingIds - The loading id or ids to autmoatically start and stop
   * @param action - The action to perform, if this returns a promise, then the done() action will be added to the finally() of the promise.
   */
  public whileLoading<TReturn>(loadingIds: T[number] | T[number][], action: () => TReturn): TReturn;
  public whileLoading<TReturn>(loadingIds: T[number] | T[number][], action: () => Promise<TReturn>): Promise<TReturn>;
  public whileLoading<TReturn>(loadingIds: T[number] | T[number][], action: () => TReturn | Promise<TReturn>): TReturn | Promise<TReturn>  {
    if (!Array.isArray(loadingIds)) {
      loadingIds = [ loadingIds ];
    }
    this.start(...loadingIds);
    let isPromiseReturn = false;
    try {
      const response = action();
      if (response instanceof Promise) {
        isPromiseReturn = true;
        const promiseLoadingIds: T[number][] = loadingIds;
        return response.finally(() => this.done(...promiseLoadingIds));
      }
      return response;
    } finally {
      if (!isPromiseReturn) {
        this.done(...loadingIds);
      }
    }
  }
}
